import { Mail, Globe, Phone, ArrowUpRight, Instagram, Linkedin, Twitter, Facebook, Youtube } from "lucide-react";
import { Wordmark } from "@/components/site/Logo";
import { scrollToId } from "@/lib/motion";
import { track } from "@/lib/analytics";

const NAV = [
  { name: "Home", id: "hero" }, { name: "Services", id: "services" },
  { name: "Our Work", id: "projects" }, { name: "About", id: "about" }, { name: "Contact", id: "quote" },
];

const SOCIAL_ICONS = { instagram: Instagram, linkedin: Linkedin, twitter: Twitter, facebook: Facebook, youtube: Youtube };

export default function Footer({ settings }) {
  const socials = settings?.socials || {};
  const email = settings?.business_email && settings.business_email.trim();
  const phone = settings?.phone && settings.phone.trim();
  const website = settings?.website_display && settings.website_display.trim();
  const activeSocials = Object.entries(socials).filter(([, v]) => v && v.trim());
  const hasContact = email || phone || website || activeSocials.length > 0;

  return (
    <footer data-testid="site-footer" className="relative bg-navy-deep text-slate-400 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="mb-6"><Wordmark light iconSize={44} /></div>
            <p className="max-w-sm leading-relaxed text-slate-400">{settings?.footer_contact}</p>
            <button onClick={() => scrollToId("quote")}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500 transition-colors">
              Request a Quote <ArrowUpRight size={16} />
            </button>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">Navigate</div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.id}>
                  <button onClick={() => scrollToId(n.id)} data-testid={`footer-nav-${n.id}`}
                    className="hover:text-white transition-colors">{n.name}</button>
                </li>
              ))}
            </ul>
          </div>

          {hasContact && (
          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">Get in touch</div>
            <ul className="space-y-4">
              {email && (
                <li>
                  <a href={`mailto:${email}`} data-testid="footer-email" onClick={() => track("contact_click", { section: "footer-email" })} className="inline-flex items-center gap-3 hover:text-white transition-colors">
                    <Mail size={16} className="text-brand-cyan" /> {email}
                  </a>
                </li>
              )}
              {phone && (
                <li>
                  <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-3 hover:text-white transition-colors" data-testid="footer-phone">
                    <Phone size={16} className="text-brand-cyan" /> {phone}
                  </a>
                </li>
              )}
              {website && (
                <li className="inline-flex items-center gap-3" data-testid="footer-website">
                  <Globe size={16} className="text-brand-cyan" /> {website}
                </li>
              )}
            </ul>
            {activeSocials.length > 0 && (
              <div className="mt-6 flex gap-3">
                {activeSocials.map(([k, v]) => {
                  const Icon = SOCIAL_ICONS[k] || Globe;
                  return (
                    <a key={k} href={v} target="_blank" rel="noopener noreferrer"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 hover:border-brand-blue hover:text-white transition-colors">
                      <Icon size={17} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} NEEC Digital. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
